import { useFormState } from 'react-final-form';

import { Icons } from '@cast/design-system';

import { RouteLeaveGuard } from 'components/router';

import { useConfirmationDialog } from '../../_hooks/useConfirmationDialog';

type Props = {
  /**
   * Pathname suffixes that may be navigated freely without confirmation
   * (e.g. switching between vertical and horizontal settings tabs).
   */
  allowedPathSuffixes: string[];
};

const normalize = (pathname: string) => pathname.replace(/\/+$/, '');

const pathEndsWithAllowedSuffix = (
  pathname: string,
  allowedPathSuffixes: string[]
) =>
  allowedPathSuffixes.some(
    (suffix) =>
      normalize(pathname) === suffix ||
      normalize(pathname).endsWith(`/${suffix}`)
  );

/**
 * Blocks leaving rightsizing settings routes when the form is dirty,
 * except when navigating between allowed settings suffixes.
 */
export const RightsizingSettingsRouteGuard = ({
  allowedPathSuffixes,
}: Props) => {
  const { openConfirmationDialog, closeConfirmationDialog } =
    useConfirmationDialog();

  const { dirty, submitSucceeded, dirtySinceLastSubmit } = useFormState({
    subscription: {
      dirty: true,
      submitSucceeded: true,
      dirtySinceLastSubmit: true,
    },
  });

  // submitSucceeded stays true after a successful save; fall back to
  // dirtySinceLastSubmit so edits made after saving still trigger confirmation.
  const hasUnsavedChanges = submitSucceeded ? dirtySinceLastSubmit : dirty;

  return (
    <RouteLeaveGuard
      askBeforeLeaving={({ nextLocation }) => {
        // Allow navigation to settings tabs under the same layout that
        // mounted this guard — the layout owns the shared form.
        // Any other navigation requires confirmation when the form is dirty.
        if (
          pathEndsWithAllowedSuffix(nextLocation.pathname, allowedPathSuffixes)
        ) {
          return false;
        }
        return hasUnsavedChanges;
      }}
      guard={(onAllow, onDecline) => {
        openConfirmationDialog({
          title: 'Are you sure you want to leave without saving?',
          body: 'Any unsaved changes will be lost.',
          variant: 'check',
          confirmButtonProps: {
            onClick: () => {
              onDecline();
              closeConfirmationDialog();
            },
          },
          cancelButtonProps: {
            onClick: () => {
              onAllow();
              closeConfirmationDialog();
            },
          },
          confirmBtnLabel: 'Continue editing',
          cancelBtnLabel: 'Leave',
          icon: <Icons.Question />,
        });
      }}
    />
  );
};
